// Pure helpers for deciding whether an editor form differs from its last-saved
// state, and what the sessionStorage draft mirror should do on a given render.

/** '', null and undefined are all "empty" and equal to each other. The editor
 *  forms initialise optional fields as `value ?? ""`, so a never-touched field
 *  would otherwise read as dirty the moment the form mounts. */
export function isEmptyish(value: unknown): boolean {
  return value === "" || value === null || value === undefined;
}

function canonical(value: unknown): unknown {
  if (isEmptyish(value)) return null;
  if (Array.isArray(value)) return value.map(canonical);
  if (typeof value === "object") {
    const input = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(input).sort()) {
      out[key] = canonical(input[key]);
    }
    return out;
  }
  return value;
}

function valuesEqual(left: unknown, right: unknown): boolean {
  if (isEmptyish(left) && isEmptyish(right)) return true;
  return JSON.stringify(canonical(left)) === JSON.stringify(canonical(right));
}

/** Number of top-level keys whose values differ, emptiness-normalised.
 *  Nested objects/arrays compare by JSON.stringify of a key-sorted projection,
 *  so array order is significant (reordering items counts as a change). */
export function diffCount(
  saved: Record<string, unknown>,
  draft: Record<string, unknown>
): number {
  const keys = new Set([...Object.keys(saved), ...Object.keys(draft)]);
  let count = 0;
  for (const key of keys) {
    if (!valuesEqual(saved[key], draft[key])) count += 1;
  }
  return count;
}

export function isDirty(
  saved: Record<string, unknown>,
  draft: Record<string, unknown>
): boolean {
  return diffCount(saved, draft) > 0;
}

/** What the sessionStorage draft mirror should do on a given render.
 *
 *  `clear` only when this visit actually wrote a mirror: a freshly mounted
 *  editor is always clean, so clearing on cleanliness alone would silently
 *  delete a restore offer the user navigated past without acting on. */
export type MirrorAction = "write" | "clear" | "none";

export function mirrorAction(dirty: boolean, hasMirrored: boolean): MirrorAction {
  if (dirty) return "write";
  return hasMirrored ? "clear" : "none";
}

/** Whether the unmount / key-change cleanup should persist the draft. */
export function shouldFlushOnLeave(dirty: boolean, discarded: boolean): boolean {
  return dirty && !discarded;
}
